import { html, property } from 'lit-element';
import { KatLitElement, register } from '../../shared/base';
import { KatOption } from '../dropdown/option';
import baseStyles from '../../shared/base/base.lit.scss';
import styles from './region-selector.lit.scss';
import getString from './strings';

/**
 * @component {kat-region-selector} KatRegionSelector Dropdown for selecting a supported country/region.
 * @event change Emits after the region-selector value has changed.
 * @event expand Emits whenever the region-selector is expanded.
 * @event collapse Emits whenever the region-selector is collapsed.
 * @status Production
 * @theme flo
 * @example BasicSingle {"value": "US"}
 * @example BasicMultiple {"multiple": true, "values": "['US', 'BR']"}
 * @example UsingInclude {"include": "['US', 'IN', 'BR', 'AE']"}
 * @example UsingExclude {"exclude": "['US', 'BR']"}
 */

interface Country {
  id: string;
  name: string;
  icon: string;
  regionId: Region['id'];
}

interface Region {
  id: 'AMER' | 'APAC' | 'EURO' | 'MENA';
  label: string;
}

export const SUPPORTED_COUNTRIES: Country[] = [
  {
    id: 'BR',
    name: getString('region-selector-brazil'),
    icon: 'flag-br',
    regionId: 'AMER',
  },
  {
    id: 'CA',
    name: getString('region-selector-canada'),
    icon: 'flag-ca',
    regionId: 'AMER',
  },
  {
    id: 'MX',
    name: getString('region-selector-mexico'),
    icon: 'flag-mx',
    regionId: 'AMER',
  },
  {
    id: 'US',
    name: getString('region-selector-united-states'),
    icon: 'flag-us',
    regionId: 'AMER',
  },
  {
    id: 'AU',
    name: getString('region-selector-australia'),
    icon: 'flag-au',
    regionId: 'APAC',
  },
  {
    id: 'IN',
    name: getString('region-selector-india'),
    icon: 'flag-in',
    regionId: 'APAC',
  },
  {
    id: 'JP',
    name: getString('region-selector-japan'),
    icon: 'flag-jp',
    regionId: 'APAC',
  },
  {
    id: 'SG',
    name: getString('region-selector-singapore'),
    icon: 'flag-sg',
    regionId: 'APAC',
  },
  {
    id: 'BG',
    name: getString('region-selector-belgium'),
    icon: 'flag-bg',
    regionId: 'EURO',
  },
  {
    id: 'FR',
    name: getString('region-selector-france'),
    icon: 'flag-fr',
    regionId: 'EURO',
  },
  {
    id: 'DE',
    name: getString('region-selector-germany'),
    icon: 'flag-de',
    regionId: 'EURO',
  },
  {
    id: 'IT',
    name: getString('region-selector-italy'),
    icon: 'flag-it',
    regionId: 'EURO',
  },
  {
    id: 'NL',
    name: getString('region-selector-netherlands'),
    icon: 'flag-nl',
    regionId: 'EURO',
  },
  {
    id: 'PL',
    name: getString('region-selector-poland'),
    icon: 'flag-pl',
    regionId: 'EURO',
  },
  {
    id: 'ES',
    name: getString('region-selector-spain'),
    icon: 'flag-es',
    regionId: 'EURO',
  },
  {
    id: 'SE',
    name: getString('region-selector-sweden'),
    icon: 'flag-se',
    regionId: 'EURO',
  },
  {
    id: 'TR',
    name: getString('region-selector-turkey'),
    icon: 'flag-tr',
    regionId: 'EURO',
  },
  {
    id: 'UK',
    name: getString('region-selector-united-kingdom'),
    icon: 'flag-uk',
    regionId: 'EURO',
  },
  {
    id: 'EG',
    name: getString('region-selector-egypt'),
    icon: 'flag-eg',
    regionId: 'MENA',
  },
  {
    id: 'SA',
    name: getString('region-selector-saudi-arabia'),
    icon: 'flag-sa',
    regionId: 'MENA',
  },
  {
    id: 'AE',
    name: getString('region-selector-united-arab-emirates'),
    icon: 'flag-ae',
    regionId: 'MENA',
  },
];

const SUPPORTED_REGIONS: Region[] = [
  {
    id: 'AMER',
    label: getString('region-selector-americas'),
  },
  {
    id: 'APAC',
    label: getString('region-selector-asia-pacific'),
  },
  {
    id: 'EURO',
    label: getString('region-selector-europe'),
  },
  {
    id: 'MENA',
    label: getString('region-selector-middle-east-and-north-africa'),
  },
];

// Creates type that enforces array must be made of strings from IDs of Countries.
type SupportedCountry = typeof SUPPORTED_COUNTRIES[number]['id'];

@register('kat-region-selector')
export class KatRegionSelector extends KatLitElement {
  static get styles() {
    return [baseStyles, styles];
  }

  /** Indicates whether the region selector should allow multiple selections. */
  @property({ reflect: true })
  multiple?: boolean;

  /** Array of country IDs to include. Unsupported countries are ignored. Cannot be combined with exclude. Defaults to all supported country IDs. */
  @property({ reflect: true })
  include?: SupportedCountry[];

  /** Array of country IDs to exclude. Unsupported countries are ignored. Cannot be combined with include. Defaults to [] */
  @property({ reflect: true })
  exclude?: SupportedCountry[];

  /** Setting disabled stops interactivity. */
  @property({ reflect: true })
  disabled?: boolean;

  /**
   * Setting the state of the input changes its look to give the user more information about what they have entered. This value must be set for State Labels to show up.
   * @enum {value} error Lets the user know there is a problem with the value they have entered in the input.
   */
  @property()
  state?: string;

  /** Optional pretext for the state label. Provides additional context to the state for the input. */
  @property({ attribute: 'state-emphasis' })
  stateEmphasis?: string;

  /** Provides users with more information about why the input is in the state it is in. */
  @property({ attribute: 'state-label' })
  stateLabel?: string;

  /** Whether the component is currently showing its dropdown options. */
  @property()
  expanded?: boolean;

  /**
   * The max height of the selection area.
   * This should be a css value with units, e.g. "8px".
   */
  @property({ attribute: 'max-height' })
  maxHeight?: string;

  /**
   * Locale to identify the language used in fixed labels.
   * Use this property/attribute to override the automatic language detection.
   * @type {string}
   */
  @property({ reflect: true })
  locale?: string;

  /** The value of the selected option. Propagated from the internal KatDropdown. */
  @property({ reflect: true })
  value?: string;

  /** The value of the selected options if multiple. Propagated from the internal KatDropdown. */
  @property({ type: Array, reflect: true })
  values?: string[];

  constructor() {
    super();

    this.updateOptions();
  }

  // Updates when the options are filtered OR a value is set
  shouldUpdate(changingProperties: Map<string, any>): boolean {
    const hasIncludeOrExclude =
      changingProperties.has('include') || changingProperties.has('exclude');

    if (hasIncludeOrExclude) {
      this.updateOptions();
    }

    // Stop an invalid value from being set
    if (changingProperties.has('value') && this.isValueInvalid()) {
      this.value = changingProperties.get('value');
    }

    // filter out invalid values from being set
    if (changingProperties.has('values')) {
      this.values = this.validateValues();
    }

    return true;
  }

  // Clears value/values if updated include/exclude makes it invalid
  updated(changedProperties: Map<string, any>) {
    const hasIncludeOrExclude =
      changedProperties.has('include') || changedProperties.has('exclude');

    if (hasIncludeOrExclude) {
      if (this.multiple) {
        this.values = this.validateValues();
      } else if (this.isValueInvalid()) {
        this.value = '';
      }
    }
  }

  /**
   * Used for single select only.
   * @returns Boolean value for validity of selected option; controlled by selected option's presence in available options list.
   */
  private isValueInvalid(): boolean {
    if (!this.value) {
      return false;
    }

    if (this.multiple) {
      return true;
    }

    return !this.availableCountries.some(
      availableCountry => availableCountry.id === this.value
    );
  }

  /**
   * Used for multi-select only.
   * @returns The list of valid selected country ids from those that have been attempted to be selected.
   */
  private validateValues(): Country['id'][] {
    if (!this.values || !this.multiple) {
      return [];
    }

    return this.values.filter(value =>
      this.availableCountries.find(country => country.id === value)
    );
  }

  private availableCountries: Country[];

  private updateOptions() {
    const hasInclude = this.include?.length > 0;
    const hasExclude = this.exclude?.length > 0;
    let countries = SUPPORTED_COUNTRIES;

    if (hasInclude && hasExclude) {
      console.warn(
        'Using both `include` and `exclude` attributes is not supported.'
      );
    }

    if (hasInclude) {
      countries = SUPPORTED_COUNTRIES.filter(country =>
        this.include.includes(country.id)
      );
    } else if (hasExclude) {
      countries = SUPPORTED_COUNTRIES.filter(
        country => !this.exclude.includes(country.id)
      );
    }

    this.availableCountries = countries;
  }

  private handleChange(e: { detail: { value?: string; values?: string[] } }) {
    if (e.detail.value) {
      this.value = e.detail.value;
    } else {
      this.values = e.detail.values;
    }
  }

  private selectionSummaryOverride(option: KatOption) {
    return option.value;
  }

  private renderCountry(country: Country) {
    return html`
      <kat-option value=${country.id}>
        <div class="country-option">
          <kat-icon
            class="country-icon"
            name=${country.icon}
            size="small"
          ></kat-icon>
          <span class="country-name">${country.name}</span>
          <span class="country-code">(${country.id.toUpperCase()})</span>
        </div>
      </kat-option>
    `;
  }

  private renderRegion(regionId: Region['id']) {
    const availableCountriesInRegion: Country[] = [];
    this.availableCountries.forEach(country => {
      if (country.regionId === regionId) {
        availableCountriesInRegion.push(country);
      }
    });

    const renderedCountriesInRegion = availableCountriesInRegion.map(country =>
      this.renderCountry(country)
    );

    const regionLabel = SUPPORTED_REGIONS.find(
      region => region.id === regionId
    ).label;

    return html`
      <kat-optgroup>
        <span slot="label">${regionLabel}</span>
        ${renderedCountriesInRegion}
      </kat-optgroup>
    `;
  }

  render() {
    const availableRegions: Region['id'][] = [];

    this.availableCountries.forEach(country => {
      if (!availableRegions.includes(country.regionId)) {
        availableRegions.push(
          SUPPORTED_REGIONS.find(region => region.id === country.regionId).id
        );
      }
    });

    const placeholderString = getString(
      'region-selector-placeholder',
      null,
      this.locale,
      {
        quantity: this.availableCountries.length === 1 ? 'one' : 'other',
        replacers: [['0', this.availableCountries.length.toString()]],
      }
    );

    const renderedRegions = availableRegions.map(region =>
      this.renderRegion(region)
    );

    return html`
      <kat-dropdown
        searchable
        multiple-show-apply=${this.multiple}
        multiple-sticky-apply=${this.multiple}
        placeholder=${placeholderString}
        ?multiple=${this.multiple}
        ?rich-selection-label=${!this.multiple}
        ?disabled=${this.disabled}
        ?expanded=${this.expanded}
        .value=${this.value}
        .values=${this.values}
        .state=${this.state}
        .stateEmphasis=${this.stateEmphasis}
        .stateLabel=${this.stateLabel}
        .maxHeight=${this.maxHeight}
        .selectionSummaryOverride=${this.selectionSummaryOverride}
        @change=${this.handleChange}
      >
        <div>${renderedRegions}</div>
        <div></div>
        <!-- I'm not sure why this div is required, but options render outside the dropdown without it -->
      </kat-dropdown>
    `;
  }
}
